import { SkillLevelValuesEnum, SportsEnum, useGetSkillLevelsQuery } from 'types/generated/client';
import { getSportIconSrc } from 'components/SportBadge';
import classNames from 'styles/utils/classNames';

interface Props {
  level: SkillLevelValuesEnum;
  /**
   * Overrides the looked-up skill-level label — e.g. a numeric scale rating
   * like "NTRP 4.0" or "VAIR 4.0". Colour still comes from `level`.
   */
  label?: string;
  /** `xs` is 10px; defaults to `sm` (12px) so existing call sites are unchanged. */
  size?: 'xs' | 'sm';
  /** Steps the label + glyph up at `lg`, like Button's `sizeDesktop`. */
  sizeDesktop?: 'xs' | 'sm';
  /**
   * `tinted` (default) — level-coloured background and text.
   * `neutral` — white surface with border and drop shadow (as SportBadge's neutral).
   */
  variant?: 'tinted' | 'neutral';
  /** Leads with the sport's glyph (the one SportBadge renders), e.g. "🏓 Brand new". */
  sport?: SportsEnum;
  className?: string;
}

const STYLES: Partial<Record<SkillLevelValuesEnum, string>> = {
  [SkillLevelValuesEnum.BrandNew]: 'bg-brand-grass-100 text-brand-grass-700',
  [SkillLevelValuesEnum.Beginner]: 'bg-brand-yellow-100 text-brand-yellow-700',
  [SkillLevelValuesEnum.Intermediate]: 'bg-brand-fire-100 text-brand-fire-700',
  [SkillLevelValuesEnum.Advanced]: 'bg-brand-red-100 text-brand-red-600',
  [SkillLevelValuesEnum.Pro]: 'bg-brand-purple-100 text-brand-purple-600',
};

export default function BadgeRating({
  level,
  label,
  size = 'sm',
  sizeDesktop,
  variant = 'tinted',
  sport,
  className,
}: Props) {
  // Skip the lookup entirely when the caller supplies its own label.
  const { data } = useGetSkillLevelsQuery({ fetchPolicy: 'cache-first', skip: !!label });
  const resolvedLabel = label ?? data?.skillLevels.find((l) => l.id === level)?.displayName ?? '';
  const styleClass = STYLES[level];

  if (!resolvedLabel || !styleClass) return null;

  return (
    <span
      className={classNames(
        'inline-flex items-center justify-center rounded-full',
        size === 'xs'
          ? 'typography-product-button-label-xs'
          : 'typography-product-button-label-small',
        sizeDesktop === 'xs' && 'lg:typography-product-button-label-xs',
        sizeDesktop === 'sm' && 'lg:typography-product-button-label-small',
        variant === 'neutral' && sizeDesktop === 'sm' && 'lg:h-6',
        variant === 'neutral'
          ? 'h-5 gap-1 border border-color-border-input-lightmode bg-color-bg-lightmode-primary px-ds-sm text-color-text-lightmode-primary shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08)] dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary dark:text-color-text-darkmode-primary'
          : classNames('px-1.5 py-0.5', styleClass),
        className,
      )}
    >
      {sport && (
        <img
          src={getSportIconSrc(sport)}
          alt=""
          className={classNames('h-3 w-3 shrink-0', sizeDesktop === 'sm' && 'lg:h-4 lg:w-4')}
        />
      )}
      {resolvedLabel}
    </span>
  );
}
